import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ActionIcon, EntityBadge, Flex, Icon } from '@tf/ui';
import type React from 'react';

import { ActionCard, useGetConnectionLabels, useGetEntityLabel } from '@/components/shared';
import { getEntityForms } from '@/components/structure/getEntityForms';
import { useModalsController } from '@/components/structure/ModalsControllerContext';
import type { ConnectedEntity, StructureEntity } from '@/components/structure/useStructureEntities';
import { useApplicationNodeIdParam } from '@/routes/endClientArea/params';

type ConnectedEntityWithKind = ConnectedEntity & { connectionKind: string };

interface Props {
	connectedEntities: ConnectedEntityWithKind[];
	entities: StructureEntity[];
	mainEntity: StructureEntity;
	labelsToShow?: 'connectionKind' | 'entityKind';
}

export const ConnectedEntitiesList: React.FC<Props> = ({
	connectedEntities,
	entities,
	mainEntity,
	labelsToShow = 'entityKind',
}) => {
	const { _ } = useLingui();
	const _applicationKind = useApplicationNodeIdParam() ?? '';
	const { formModal, removeConnectionModal } = useModalsController() ?? {};

	const getConnectionLabels = useGetConnectionLabels(mainEntity.entityKind);
	const getEntityLabel = useGetEntityLabel();

	return (
		<>
			{connectedEntities.map((entityItem) => {
				const entity = entities.find((e) => e.entityId === entityItem.entityId);
				const formsContext = {
					entityId: entityItem.entityId,
					parentEntityId: mainEntity.entityId,
					connectionKind: entityItem.connectionKind,
				};
				const statuses = getEntityForms(entities, formsContext)?.map((f) => f.formStatus.structure);
				const hasErrors = statuses?.some((s) => s !== 'OK');
				const label =
					labelsToShow === 'connectionKind'
						? getConnectionLabels(entityItem.connectionKind)?.singular
						: getEntityLabel(entity?.entityKind ?? '');

				return (
					<ActionCard
						error={
							hasErrors
								? _(
										msg({
											id: 'structure.connectedEntitiesList.errorText',
											message: `Please add all required information`,
										}),
									)
								: undefined
						}
						key={entityItem.entityId}
						icon={
							<Flex
								justify="center"
								align="center"
								w="36px"
								h="36px"
								mr="12px"
								bg={hasErrors ? '#FEE2E2' : '#F3F4F6'}
								sx={{ borderRadius: '50%' }}
							>
								{entity?.entityKind === 'INDIVIDUAL_ENTITY' ? (
									<Icon.IconUser size={24} />
								) : (
									<Icon.IconBuildingSkyscraper size={24} />
								)}
							</Flex>
						}
						title={entityItem.entityName}
						onClick={() => formModal?.open(formsContext)}
					>
						<Flex sx={{ flex: 1 }} align="center" justify="flex-end">
							<EntityBadge kind={label} />
							<ActionIcon
								ml="12px"
								variant="transparent"
								c="gray.5"
								onClick={(event) => {
									event.stopPropagation();
									removeConnectionModal?.open({
										parentEntityName: mainEntity.entityName,
										parentEntityId: mainEntity.entityId,
										entityId: entityItem.entityId,
										connectionKind: entityItem.connectionKind,
									});
								}}
							>
								<Icon.IconTrash size={18} stroke={1.5} />
							</ActionIcon>
						</Flex>
					</ActionCard>
				);
			})}
		</>
	);
};
